/**
 * Busca dos dados de nível dos rios.
 *
 * Uma origem só: o cache publicado em `AmaView-dados`, no GitHub Pages.
 *
 * Aqui a regra de não falar com a fonte direto do navegador não é só de boa
 * vizinhança — é obrigatória. O webservice da ANA é **HTTP puro, sem TLS**, e
 * uma página HTTPS no GitHub Pages bloqueia conteúdo misto. Mesmo que
 * liberasse, ele é um serviço público que degrada com 6 conexões simultâneas:
 * quem fala com ele é o workflow, uma vez por hora, com concorrência 3.
 */
import { fetchWithRetry } from '../lib/fetchRetry';
import { lonLatToMap } from '../map/projection';
import type { Classe, Dados, Estacao, EstacaoCrua, LeituraCrua, Rio, Tendencia } from './types';

const TENDENCIAS: readonly Tendencia[] = ['subindo', 'descendo', 'estavel', 'desconhecida'];
const CLASSES: readonly Classe[] = ['muitoAbaixo', 'abaixo', 'normal', 'acima', 'muitoAcima'];

/**
 * Onde o pipeline publica (ver o repositório `AmaView-dados`).
 * `VITE_RIOS_URL` aponta para outro lugar em desenvolvimento.
 */
export const BASE_DADOS = import.meta.env.VITE_RIOS_URL || 'https://helvecioneto.github.io/AmaView-dados/rios';

export const ANA_ATRIBUICAO =
  'Nível e vazão: <a href="https://www.snirh.gov.br/hidroweb/" target="_blank" rel="noopener">ANA — rede telemétrica do SNIRH</a>';

async function json(url: string, signal?: AbortSignal): Promise<unknown> {
  return fetchWithRetry<unknown>(url, {}, { signal, read: (r) => r.json() as Promise<unknown> });
}

/**
 * Cadastro + leituras do ciclo. As duas descidas são paralelas, e as leituras
 * são indexadas pelo código da estação porque a ordem pode mudar entre
 * publicações (estações entram e saem da rede telemétrica).
 */
export async function carregarDados(signal?: AbortSignal): Promise<Dados> {
  const [cruEstacoes, cruAtual] = await Promise.all([
    json(`${BASE_DADOS}/estacoes.json`, signal),
    json(`${BASE_DADOS}/atual.json`, signal),
  ]);

  const e = cruEstacoes as { estacoes?: EstacaoCrua[] };
  if (!Array.isArray(e.estacoes)) throw new Error('estacoes.json sem a lista `estacoes`');

  const a = cruAtual as {
    rios?: LeituraCrua[];
    serie?: Record<string, (number | null)[]>;
    t0?: number;
    passoMin?: number;
    slots?: number;
    gerado?: string;
    fonte?: string;
  };
  if (!Array.isArray(a.rios)) throw new Error('atual.json sem a lista `rios`');

  const estacoes = new Map<string, Estacao>();
  for (const x of e.estacoes) {
    if (typeof x.c !== 'string' || !Number.isFinite(x.y) || !Number.isFinite(x.x)) continue;
    const [mx, my] = lonLatToMap(x.x, x.y);
    if (!Number.isFinite(mx) || !Number.isFinite(my)) continue;
    estacoes.set(x.c, {
      cod: x.c,
      nome: typeof x.n === 'string' ? x.n : x.c,
      lat: x.y,
      lon: x.x,
      mx,
      my,
      temVazao: x.v === true,
      operadora: typeof x.op === 'string' && x.op.trim() ? x.op.trim() : null,
    });
  }

  const slots = Number(a.slots) || 48;
  const rios: Rio[] = [];
  let comReferencia = 0;

  for (const l of a.rios) {
    const estacao = estacoes.get(l.c);
    if (!estacao || !Number.isFinite(l.cm)) continue;

    const serieCrua = a.serie?.[l.c];
    // Série com tamanho errado é dado quebrado, não dado parcial: descartar a
    // linha é melhor que desenhar o rio na hora errada.
    const serie = Array.isArray(serieCrua) && serieCrua.length === slots ? serieCrua : new Array(slots).fill(null);

    if (l.a !== null && l.a !== undefined && CLASSES.includes(l.a)) comReferencia++;

    rios.push({
      estacao,
      cm: l.cm,
      ms: Number(l.ms) || 0,
      vazao: num(l.q),
      delta24h: num(l.d24),
      delta48h: num(l.d48),
      // Valor fora do domínio vira "desconhecida"/null, nunca "undefined" na tela.
      tendencia: TENDENCIAS.includes(l.t) ? l.t : 'desconhecida',
      classe: l.a !== null && l.a !== undefined && CLASSES.includes(l.a) ? l.a : null,
      percentil: num(l.p),
      anomaliaCm: num(l.an),
      referencia: l.ref ?? null,
      extremos: l.ext ?? null,
      bruta: l.bruta === true,
      situacao: typeof l.sit === 'string' && l.sit.trim() ? l.sit.trim() : null,
      herdada: l.h === true,
      serie,
    });
  }

  return {
    rios,
    t0: Number(a.t0) || 0,
    passoMin: Number(a.passoMin) || 60,
    slots,
    gerado: a.gerado ? Date.parse(a.gerado) || null : null,
    fonte: typeof a.fonte === 'string' ? a.fonte : null,
    comReferencia,
  };
}

function num(x: unknown): number | null {
  return typeof x === 'number' && Number.isFinite(x) ? x : null;
}
